import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, FileSignature, Mail } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';
const LOGO_URL = `${API_BASE}/uploads/logos/logo_20260410_145221.PNG`;

const SignAmendment = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [amendment, setAmendment] = useState(null);
  const [lease, setLease] = useState(null);
  const [signed, setSigned] = useState(false);
  const [signing, setSigning] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkToken();
  }, [token]);

  const checkToken = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/amendments/public/check-token/${token}`);
      if (res.data.valid) {
        setValid(true);
        setAmendment(res.data.amendment);
        setLease(res.data.lease);
      } else {
        setValid(false);
        setError('Invalid signature link');
      }
    } catch (err) {
      setValid(false);
      setError(err.response?.data?.detail || 'Invalid or expired signature link');
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async () => {
    if (!agreed) {
      alert('Please accept the terms before signing');
      return;
    }

    setSigning(true);
    try {
      // Plus d'appel à ipify - l'IP est capturée par le backend
      const response = await axios.post(`${API_BASE}/amendments/public/sign`, {
        token: token
      });
      
      setSigned(true);
      
      setTimeout(() => {
        // Rediriger vers la page d'accueil ou dashboard client
        window.location.href = '/';
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error during signing');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Verifying signature link...</p>
        </div>
      </div>
    );
  }

  if (!valid || error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800 rounded-xl max-w-md w-full p-6 text-center">
          <XCircle size={64} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-white text-xl font-bold mb-2">Invalid Signature Link</h2>
          <p className="text-gray-400 mb-6">{error || 'This link is invalid or has expired.'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800 rounded-xl max-w-md w-full p-6 text-center">
          <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
          <h2 className="text-white text-xl font-bold mb-2">Amendment Signed!</h2>
          <p className="text-gray-400 mb-6">
            Your signature has been recorded. The changes will be applied to your lease.
              You will receive a confirmation email shortly.
            </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-xl max-w-2xl w-full p-6">
        
        <div className="text-center mb-6">
          <img 
            src={LOGO_URL} 
            alt="Logo" 
            className="h-16 mx-auto mb-4" 
            onError={(e) => e.target.style.display = 'none'} 
          />
          <h1 className="text-2xl font-bold text-white">
            Sign {amendment?.amendment_type === 'addendum' ? 'Addendum' : 'Amendment'}
          </h1>
          <p className="text-gray-400">Lease {lease?.lease_number}</p>
        </div>

        <div className="bg-gray-700/50 rounded-lg p-4 mb-6">
          <h3 className="text-white font-semibold mb-2">{amendment?.title}</h3>
          <p className="text-gray-400 text-sm mb-3">Effective: {amendment?.effective_date}</p>
          
          {amendment?.changes && Object.keys(amendment.changes).length > 0 && (
            <div className="bg-gray-800 rounded p-3 mb-3">
              <p className="text-gray-400 text-xs mb-2">Changes:</p>
              {Object.entries(amendment.changes).map(([key, value]) => (
                <div key={key} className="flex justify-between text-sm py-1">
                  <span className="text-gray-400">{key}:</span>
                  <span className="text-white font-mono">{String(value)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border border-gray-700 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="agree"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-1 w-4 h-4 accent-blue-500"
            />
            <label htmlFor="agree" className="text-gray-300 text-sm">
              I confirm that I am authorized to sign on behalf of {lease?.tenant_name}. 
              I agree to the terms of this {amendment?.amendment_type} and understand 
              that it legally modifies my existing lease agreement.
            </label>
          </div>
        </div>

        <button
          onClick={handleSign}
          disabled={!agreed || signing}
          className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white font-semibold flex items-center justify-center gap-2"
        >
          <FileSignature size={20} />
          {signing ? 'Processing...' : `Sign ${amendment?.amendment_type === 'addendum' ? 'Addendum' : 'Amendment'}`}
        </button>

        <p className="text-gray-500 text-xs text-center mt-4">
          By signing, you confirm your identity and intent to be bound by these terms.
          Your IP address and timestamp will be recorded.
        </p>
      </div>
    </div>
  );
};

export default SignAmendment;